import React, { useMemo, useState } from 'react';
import { Button, Steps } from 'antd';
import { SECTIONS, sectionFor } from './review/sections.js';
import ReviewSection from './review/ReviewSection.jsx';
import FollowUpsPanel from './review/FollowUpsPanel.jsx';
import { rowState } from './review/validation.js';
import { computeExcluded } from './review/exclusions.js';

/**
 * The sequential step machine tying the per-section review pages (Task 8)
 * together: 5 record-type sections in `SECTIONS` order, then a Confirm step
 * that is the ONLY place a commit is offered. Sequential by design — a
 * freely reachable commit button would let a section go unreviewed.
 *
 * Commit contract (Task 4c, amended — authoritative over this task's own
 * original brief text): the wizard NEVER filters `proposal`. Client-side
 * filtering renumbers the array, which corrupts any `__prereq_index:N__`
 * reference (a synthetic vendor-support contact the extractor appends at
 * the end) into pointing at the wrong record — the same corruption class
 * fixed server-side twice already. Instead `onCommit` receives the FULL,
 * untouched `proposal` plus `excluded: { prerequisites: [...], dependents:
 * [...] }` — the ORIGINAL bucket indices of everything left unticked. The
 * server (`orchestrator.commit()`) drops those indices itself, after
 * building its identity-keyed index map over the full received array, so
 * original positions stay correct for `__prereq_index__` resolution.
 *
 * `committing` (optional, I1): the server's `prepared` guard only flips at
 * the END of a commit, so two rapid clicks on Commit would run two
 * concurrent commits — duplicate IT Glue records, and zero visible progress
 * on a multi-minute write. The parent page passes `committing={status ===
 * 'writing'}`; while true the Commit button is both `loading` and
 * `disabled`, and `commit()` itself also short-circuits, so a click that
 * slips through before the re-render lands still can't fire `onCommit`
 * twice.
 */
export default function OnboardingWizard({ extractResult, onCommit, committing = false }) {
  const { proposal, schemas = {}, validation = [], follow_ups: followUps = [] } = extractResult;
  const [step, setStep] = useState(0);

  // Group each bucket's items by review section, keeping the section-local
  // item list alongside `indices` — section-local i -> ORIGINAL bucket
  // index — so verdicts (keyed to the original index) and the eventual
  // excluded-indices payload can both be reconstructed without re-deriving
  // order. Items whose tool maps to no section (`sectionFor` -> null, e.g.
  // update_organization) are deliberately left out of every section: they
  // are never shown, never have tick-state, and so can never end up in
  // `excluded` — they simply ride along in `proposal` untouched.
  const bySection = useMemo(() => {
    const acc = {};
    for (const s of SECTIONS) acc[s.id] = { bucket: null, indices: [], items: [] };
    for (const bucket of ['prerequisites', 'dependents']) {
      (proposal[bucket] || []).forEach((item, index) => {
        const id = sectionFor(item.tool);
        if (!id) return;
        acc[id].bucket = bucket;
        acc[id].indices.push(index);
        acc[id].items.push(item);
      });
    }
    return acc;
  }, [proposal]);

  // Default tick-state respects `rowState(...).defaultChecked` — computed
  // with the ORIGINAL bucket index (`indices[i]`), since `validation`
  // verdicts are keyed to that, not the section-local position. Rows the
  // backend flagged `error` default to unticked (see validation.js) so a
  // rejected record can't reach IT Glue unnoticed.
  const [selected, setSelected] = useState(() => {
    const init = {};
    for (const s of SECTIONS) {
      const { bucket, indices } = bySection[s.id];
      init[s.id] = new Set(
        indices
          .map((_, i) => i)
          .filter((i) => rowState(bucket, indices[i], validation).defaultChecked),
      );
    }
    return init;
  });

  // The kept-set -> excluded-indices complement is delegated to
  // computeExcluded (review/exclusions.js) rather than re-derived inline —
  // that helper is the one, independently unit-tested place this logic
  // lives. It computes the complement over a FULL array given a set of KEPT
  // indices in that array's own index space; the wizard's tick-state is
  // section-local, so it is applied PER SECTION (totalCount = this
  // section's item count, keptIndices = this section's local selection),
  // and each resulting local excluded index is then mapped through
  // `indices` to recover the original bucket position. Sections sharing a
  // bucket (e.g. two sections both drawing from `prerequisites`) each
  // contribute their own local complement into the same `excluded[bucket]`
  // list.
  const commit = () => {
    if (committing) return; // belt-and-braces: see the `committing` doc above.
    const excluded = { prerequisites: [], dependents: [] };
    for (const s of SECTIONS) {
      const { bucket, indices, items } = bySection[s.id];
      if (!bucket) continue;
      const localExcluded = computeExcluded(items.length, selected[s.id]);
      localExcluded.forEach((i) => excluded[bucket].push(indices[i]));
    }
    onCommit({ proposal, excluded });
  };

  const isConfirm = step >= SECTIONS.length;
  const section = SECTIONS[step];

  return (
    <div>
      <Steps
        current={step}
        items={[...SECTIONS.map((s) => ({ title: s.title })), { title: 'Confirm' }]}
      />

      {isConfirm ? (
        <div>
          <h2>Confirm</h2>
          {SECTIONS.map((s) => (
            <div key={s.id}>
              {s.title}: {selected[s.id].size} of {bySection[s.id].items.length}
            </div>
          ))}
          <FollowUpsPanel followUps={followUps} />
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Button onClick={() => setStep(Math.max(0, step - 1))} disabled={committing}>Back</Button>
            <Button type="primary" loading={committing} disabled={committing} onClick={commit}>
              Commit to IT Glue
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ReviewSection
            section={section}
            items={bySection[section.id].items}
            schemas={schemas}
            verdicts={validation}
            bucket={bySection[section.id].bucket || 'prerequisites'}
            indices={bySection[section.id].indices}
            selected={selected[section.id]}
            onSelectionChange={(next) => setSelected({ ...selected, [section.id]: next })}
            onBack={() => setStep(Math.max(0, step - 1))}
            onContinue={() => setStep(step + 1)}
          />
          {section.id === 'passwords' && <FollowUpsPanel followUps={followUps} />}
        </>
      )}
    </div>
  );
}
